// @file Composable for handling drag and drop events on the whiteboard's canvas
import { ContentPickerSource } from '@@/bits/content_picker'
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import type { TLPageId } from 'tldraw'
import type { Ref } from 'vue'
import { onMounted, onUnmounted, ref } from 'vue'

function useWhiteboardDragAndDrop(tldrawAppRef: Ref<HTMLElement | undefined>): {
  isDragging: Ref<boolean>
} {
  const isDragging = ref(false)
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardStore = useWhiteboardStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardToolbarStore = useWhiteboardToolbarStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  function startDragging(): void {
    isDragging.value = true
  }

  function stopDragging(): void {
    isDragging.value = false
    whiteboardToolbarStore.resetDropOverlayMessage()
  }

  function handleDragEnter(event: DragEvent): void {
    // Don't show file drop overlay during frame reordering
    if (whiteboardStore.isFrameDragging) {
      return
    }

    if (
      whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId) &&
      whiteboardToolsStore.isToolEnabled(ContentPickerSource.FileBrowser)
    ) {
      event.preventDefault()
      startDragging()
    }
  }

  function handleDrop(event: DragEvent): void {
    stopDragging()
    if (
      event.dataTransfer?.files?.length &&
      whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId) &&
      !whiteboardToolsStore.isToolEnabled(ContentPickerSource.FileBrowser)
    ) {
      globalSnackbarStore.setSnackbar({
        message: __('Upload is currently disabled on this sandbox'),
        notificationType: SnackbarNotificationType.error,
      })
    }
  }

  function handleDragLeave(event: DragEvent): void {
    if (tldrawAppRef.value == null) return
    const rect = tldrawAppRef.value.getBoundingClientRect()
    const isOutsideCanvas =
      event.clientX <= rect.left ||
      event.clientX >= rect.right ||
      event.clientY <= rect.top ||
      event.clientY >= rect.bottom

    if (isOutsideCanvas) {
      stopDragging()
    }
  }

  onMounted(() => {
    tldrawAppRef.value?.addEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.addEventListener('drop', handleDrop)
    tldrawAppRef.value?.addEventListener('dragleave', handleDragLeave)
  })

  onUnmounted(() => {
    tldrawAppRef.value?.removeEventListener('dragenter', handleDragEnter)
    tldrawAppRef.value?.removeEventListener('drop', handleDrop)
    tldrawAppRef.value?.removeEventListener('dragleave', handleDragLeave)
  })

  return {
    isDragging,
  }
}
export { useWhiteboardDragAndDrop }
